'use client';

// Client-bundle rule: this file imports only React, next/*, lucide-react, the site's own client-safe pieces and
// lib/site/labels (no catalogue, lib/pdf, product-type, fs, path or services).
import React, { useCallback, useRef, useState } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ButtonLink } from './Button';
import type { EventName, TrackProps } from '@/utils/analytics-events';
import { INPUT_PHOTO_LABEL } from '@/lib/site/labels';

export type SamplePage = { src: string; alt: string; width: number; height: number };

const FOCUS =
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-marigold-deep';
const SWIPE_PX = 40;

/**
 * Flip-through of a sample's real pages: prev/next buttons, ←/→ keys, swipe. Never links the PDF.
 *
 * The stage has a FIXED aspect ratio (the widest page's) so flipping between differently shaped pages (cover vs
 * spread) never resizes it or shifts the layout; each page is object-contain, centred. All pages stay mounted and
 * inactive ones are hidden with opacity/visibility (never display:none, which blocks image fetches). The current
 * page and its neighbours load eagerly; the rest prefetch at low priority once the first image has loaded.
 * A viewer with `priority={false}` (several share a page) stays lazy + low priority until the visitor focuses or flips
 * it, so it never competes with the first viewer's LCP image.
 */
export default function SampleViewer({ title, pages, cta, inputPhoto, sizes = '(max-width: 768px) 100vw, 50vw', priority = true }: {
  title: string;
  pages: SamplePage[];
  cta?: { href: string; label: string; event?: { name: EventName; props?: TrackProps } };
  /** The sample family's input photo; always shown with the "Sample family (AI-generated photo)" label. */
  inputPhoto?: { src: string; alt?: string };
  sizes?: string;
  /** Preload the first page (default). */
  priority?: boolean;
}) {
  const [index, setIndex] = useState(0);
  const [warm, setWarm] = useState(false);
  const startX = useRef<number | null>(null);
  const total = pages.length;
  const go = useCallback((delta: number) => { setWarm(true); setIndex((i) => Math.min(total - 1, Math.max(0, i + delta))); }, [total]);

  if (total === 0) return null;

  // Widest bounding box: the max width/height ratio, so landscape spreads fit and portrait covers letterbox.
  const ratio = Math.max(...pages.map((p) => (p.height > 0 ? p.width / p.height : 1)));
  const stageStyle: React.CSSProperties = {
    aspectRatio: String(Number(ratio.toFixed(4))),
    maxWidth: `calc(80svh * ${Number(ratio.toFixed(4))})`,
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  };
  const onPointerDown = (e: React.PointerEvent) => { startX.current = e.clientX; };
  const onPointerUp = (e: React.PointerEvent) => {
    if (startX.current === null) return;
    const dx = e.clientX - startX.current;
    startX.current = null;
    if (Math.abs(dx) >= SWIPE_PX) go(dx < 0 ? 1 : -1);
  };

  const btn = `inline-flex h-11 w-11 items-center justify-center rounded-full border-2 border-ink bg-paper-raised text-ink disabled:opacity-40 ${FOCUS}`;

  return (
    <div>
      {inputPhoto ? (
        <figure className="mb-4 inline-block rotate-[-2deg] bg-paper-raised p-2 shadow-[var(--shadow-photo)] motion-reduce:rotate-0">
          <div className="relative aspect-[4/3] w-44 sm:w-56">
            <Image src={inputPhoto.src} alt={inputPhoto.alt ?? INPUT_PHOTO_LABEL} fill sizes="224px" className="object-cover" />
          </div>
          <figcaption className="mt-1 text-xs font-semibold text-ink-muted">{INPUT_PHOTO_LABEL}</figcaption>
        </figure>
      ) : null}
      <section
        aria-roledescription="carousel"
        aria-label={title}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onFocus={() => setWarm(true)}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { startX.current = null; }}
        className={`touch-pan-y select-none rounded-tile bg-tile p-[18px] ${FOCUS}`}
      >
        <div className="relative mx-auto w-full" style={stageStyle} data-testid="sample-stage">
          {pages.map((p, i) => {
            const active = i === index;
            const near = Math.abs(i - index) <= 1;
            const first = priority && i === 0;
            return (
              <div
                key={p.src}
                aria-hidden={active ? undefined : true}
                className={`absolute inset-0 motion-safe:transition-opacity motion-safe:duration-150 ${
                  active ? 'opacity-100' : 'pointer-events-none invisible opacity-0'
                }`}
              >
                <Image
                  src={p.src}
                  alt={p.alt}
                  fill
                  sizes={sizes}
                  priority={first}
                  loading={first ? undefined : (near && priority) || warm ? 'eager' : 'lazy'}
                  fetchPriority={first || (near && priority) ? undefined : 'low'}
                  onLoad={i === 0 && priority ? () => setWarm(true) : undefined}
                  draggable={false}
                  className="object-contain drop-shadow-[0_6px_14px_rgba(0,0,0,0.18)]"
                />
              </div>
            );
          })}
        </div>
      </section>
      <div className="mt-3 flex items-center justify-between gap-3">
        <button type="button" className={btn} onClick={() => go(-1)} disabled={index === 0} aria-label="Previous page">
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <p aria-live="polite" className="text-sm text-ink-muted">
          Page {index + 1} of {total}
        </p>
        <button type="button" className={btn} onClick={() => go(1)} disabled={index === total - 1} aria-label="Next page">
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      {cta ? (
        <div className="mt-4">
          <ButtonLink href={cta.href} event={cta.event}>{cta.label}</ButtonLink>
        </div>
      ) : null}
    </div>
  );
}
